import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminHeader from '../../components/admin/AdminHeader';
import { buildApiUrl } from '../../config/api';

const AdminWallet = () => {
    const [users, setUsers] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [bonusAmount, setBonusAmount] = useState('');
    const [bonusDescription, setBonusDescription] = useState('');
    const [activeTab, setActiveTab] = useState('users');
    const [searchTerm, setSearchTerm] = useState('');
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalBalance: 0,
        totalCredited: 0,
        totalDebited: 0
    });

    const getToken = () => localStorage.getItem('token');

// src/pages/admin/AdminWallet.js - Fix fetchWalletData to get correct user IDs

const fetchWalletData = useCallback(async () => {
    try {
        setLoading(true);
        const token = getToken();
        
        // ✅ Fetch all users with wallets
        const usersResponse = await axios.get(buildApiUrl('/admin/wallet/users'), {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        
        console.log('Users response:', usersResponse.data);
        
        if (usersResponse.data.success) {
            // ✅ Ensure each user has correct ID
            const usersData = usersResponse.data.data.map(user => ({
                id: user.user_id,  // Make sure this matches your DB column
                user_id: user.user_id,
                name: user.name,
                email: user.email,
                phone: user.phone,
                balance: user.balance,
                total_credited: user.total_credited,
                total_debited: user.total_debited
            }));
            
            setUsers(usersData);
            
            // Calculate stats
            const totalBalance = usersData.reduce((sum, u) => sum + parseFloat(u.balance || 0), 0);
            const totalCredited = usersData.reduce((sum, u) => sum + parseFloat(u.total_credited || 0), 0);
            const totalDebited = usersData.reduce((sum, u) => sum + parseFloat(u.total_debited || 0), 0);
            
            setStats({
                totalUsers: usersData.length,
                totalBalance: totalBalance,
                totalCredited: totalCredited,
                totalDebited: totalDebited
            });
        }
        
        // Fetch recent transactions
        const transactionsResponse = await axios.get(`${buildApiUrl('/admin/wallet/transactions/all')}?limit=50`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (transactionsResponse.data.success) {
            setTransactions(transactionsResponse.data.data);
        }
        
    } catch (error) {
        console.error('Error fetching wallet data:', error);
        alert('Failed to load wallet data');
    } finally {
        setLoading(false);
    }
}, []);

    useEffect(() => {
        fetchWalletData();
    }, [fetchWalletData]);

    // src/pages/admin/AdminWallet.js - Fix handleAddBonus function

const handleAddBonus = async () => {
    // ✅ Debug: Check selected user
    console.log('Selected User:', selectedUser);
    console.log('Selected User ID:', selectedUser?.id);
    
    if (!selectedUser) {
        alert('Please select a user');
        return;
    }
    
    if (!bonusAmount || bonusAmount <= 0) {
        alert('Please enter a valid amount');
        return;
    }
    
    setLoading(true);
    
    try {
        const token = getToken();
        
        // ✅ Ensure we're sending the correct user ID
        const payload = {
            userId: parseInt(selectedUser.id),  // Make sure it's number
            amount: parseFloat(bonusAmount),
            description: bonusDescription || `Bonus added by admin to ${selectedUser.name}`
        };
        
        console.log('📡 Sending request:', payload);
        
        const response = await axios.post(buildApiUrl('/admin/wallet/add-bonus'), payload, {
            headers: { 
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });
        
        console.log('✅ Response:', response.data);
        
        if (response.data.success) {
            alert(`✅ ${response.data.message}`);
            setShowModal(false);
            setBonusAmount('');
            setBonusDescription('');
            setSelectedUser(null);
            fetchWalletData();
        } else {
            alert(response.data.message);
        }
    } catch (error) {
        console.error('❌ Error adding bonus:', error);
        console.error('Error response:', error.response?.data);
        alert(error.response?.data?.message || 'Failed to add bonus');
    } finally {
        setLoading(false);
    }
};


    const filteredUsers = users.filter(user =>
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone?.includes(searchTerm)
    );

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getTransactionTypeIcon = (type, transactionType) => {
        if (type === 'credit') {
            if (transactionType === 'bonus') return '🎁';
            if (transactionType === 'reward') return '⭐';
            if (transactionType === 'refund') return '💰';
            return '📥';
        }
        return '📤';
    };

    return (
        <div className="admin-layout">
            <AdminSidebar />
            <div className="admin-main">
                <AdminHeader title="Wallet Management" subtitle="Manage user wallets, add bonuses and rewards" />
                
                <div className="admin-wallet">
                    {/* Stats Cards */}
                    <div className="wallet-stats">
                        <div className="stat-card">
                            <div className="stat-icon total-users">
                                <i className="fas fa-users"></i>
                            </div>
                            <div className="stat-info">
                                <h3>Total Users</h3>
                                <p className="stat-value">{stats.totalUsers}</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon total-balance">
                                <i className="fas fa-wallet"></i>
                            </div>
                            <div className="stat-info">
                                <h3>Total Balance</h3>
                                <p className="stat-value">{formatCurrency(stats.totalBalance)}</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon total-credited">
                                <i className="fas fa-arrow-down"></i>
                            </div>
                            <div className="stat-info">
                                <h3>Total Credited</h3>
                                <p className="stat-value">{formatCurrency(stats.totalCredited)}</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon total-debited">
                                <i className="fas fa-arrow-up"></i>
                            </div>
                            <div className="stat-info">
                                <h3>Total Debited</h3>
                                <p className="stat-value">{formatCurrency(stats.totalDebited)}</p>
                            </div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="wallet-tabs">
                        <button className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>
                            <i className="fas fa-users"></i> User Wallets
                        </button>
                        <button className={`tab-btn ${activeTab === 'transactions' ? 'active' : ''}`} onClick={() => setActiveTab('transactions')}>
                            <i className="fas fa-history"></i> All Transactions
                        </button>
                    </div>

                    {/* Users Tab */}
                    {activeTab === 'users' && (
                        <div className="users-tab">
                            <div className="table-header">
                                <div className="search-box">
                                    <i className="fas fa-search"></i>
                                    <input
                                        type="text"
                                        placeholder="Search by name, email or phone..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="table-container">
                                {loading ? (
                                    <div className="loading-state">
                                        <div className="spinner"></div>
                                        <p>Loading users...</p>
                                    </div>
                                ) : (
                                    <table className="wallet-table">
                                        <thead>
                                            <tr>
                                                <th>User</th>
                                                <th>Balance</th>
                                                <th>Total Credited</th>
                                                <th>Total Debited</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredUsers.map(user => (
                                                <tr key={user.id}>
                                                    <td className="user-cell">
                                                        <div className="user-avatar">
                                                            {user.name?.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div className="user-details">
                                                            <strong>{user.name}</strong>
                                                            <span>{user.email}</span>
                                                            <span>{user.phone}</span>
                                                        </div>
                                                    </td>
                                                    <td className="balance-cell">
                                                        <span className="balance-amount">{formatCurrency(user.balance)}</span>
                                                    </td>
                                                    <td className="credited-cell">
                                                        <span className="credited-amount">{formatCurrency(user.total_credited)}</span>
                                                    </td>
                                                    <td className="debited-cell">
                                                        <span className="debited-amount">{formatCurrency(user.total_debited)}</span>
                                                    </td>
                                                    <td className="actions-cell">
                                                       <button 
    className="action-btn add-bonus"
    onClick={() => {
        console.log('Selected user:', user); // Debug
        setSelectedUser({
            id: user.user_id,  // Make sure this is the correct ID
            name: user.name,
            email: user.email,
            phone: user.phone,
            balance: user.balance
        });
        setShowModal(true);
    }}
>
    <i className="fas fa-gift"></i> Add Bonus
</button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {filteredUsers.length === 0 && (
                                                <tr>
                                                    <td colSpan="5" className="no-data">
                                                        <i className="fas fa-inbox"></i>
                                                        <p>No users found</p>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Transactions Tab */}
                    {activeTab === 'transactions' && (
                        <div className="transactions-tab">
                            <div className="table-container">
                                {loading ? (
                                    <div className="loading-state">
                                        <div className="spinner"></div>
                                        <p>Loading transactions...</p>
                                    </div>
                                ) : (
                                    <table className="transactions-table">
                                        <thead>
                                            <tr>
                                                <th>User</th>
                                                <th>Transaction ID</th>
                                                <th>Amount</th>
                                                <th>Type</th>
                                                <th>Description</th>
                                                <th>Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {transactions.map(tx => (
                                                <tr key={tx.id}>
                                                    <td className="user-cell">
                                                        <div className="user-avatar small">
                                                            {tx.user_name?.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div className="user-details">
                                                            <strong>{tx.user_name}</strong>
                                                            <span>{tx.user_email}</span>
                                                        </div>
                                                    </td>
                                                    <td className="tx-id">
                                                        <span className="tx-id-value">{tx.transaction_id}</span>
                                                    </td>
                                                    <td className={`amount-cell ${tx.type}`}>
                                                        {tx.type === 'credit' ? '+' : '-'} {formatCurrency(tx.amount)}
                                                    </td>
                                                    <td className="type-cell">
                                                        <span className={`tx-type ${tx.transaction_type}`}>
                                                            {getTransactionTypeIcon(tx.type, tx.transaction_type)} {tx.transaction_type}
                                                        </span>
                                                    </td>
                                                    <td className="desc-cell">{tx.description}</td>
                                                    <td className="date-cell">{formatDate(tx.created_at)}</td>
                                                </tr>
                                            ))}
                                            {transactions.length === 0 && (
                                                <tr>
                                                    <td colSpan="6" className="no-data">
                                                        <i className="fas fa-inbox"></i>
                                                        <p>No transactions found</p>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Add Bonus Modal */}
            {showModal && selectedUser && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-container" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Add Bonus to Wallet</h2>
                            <button className="close-btn" onClick={() => setShowModal(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="user-info">
                                <div className="user-avatar large">
                                    {selectedUser.name?.charAt(0).toUpperCase()}
                                </div>
                                <div className="user-info-details">
                                    <h3>{selectedUser.name}</h3>
                                    <p>{selectedUser.email}</p>
                                    <p>{selectedUser.phone}</p>
                                    <p className="current-balance">Current Balance: {formatCurrency(selectedUser.balance)}</p>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Bonus Amount (₹) *</label>
                                <input
                                    type="number"
                                    value={bonusAmount}
                                    onChange={(e) => setBonusAmount(e.target.value)}
                                    placeholder="Enter amount"
                                    min="1"
                                    step="1"
                                />
                            </div>

                            <div className="form-group">
                                <label>Description (Optional)</label>
                                <textarea
                                    value={bonusDescription}
                                    onChange={(e) => setBonusDescription(e.target.value)}
                                    placeholder="Why are you adding this bonus?"
                                    rows="3"
                                ></textarea>
                            </div>

                            <div className="modal-footer">
                                <button className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                                <button className="btn-submit" onClick={handleAddBonus}>
                                    <i className="fas fa-gift"></i> Add Bonus
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
                 <style>{`
               /* AdminWallet.css */

.admin-layout {
    display: flex;
    min-height: 100vh;
    background: #f8fafc;
}

.admin-main {
    flex: 1;
    margin-left: 280px;
    background: #f8fafc;
    padding: 2rem;
}

/* Stats Cards */
.wallet-stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem;
    margin-bottom: 2rem;
}

.stat-card {
    background: white;
    border-radius: 20px;
    padding: 1.5rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
    border: 1px solid #e2e8f0;
    transition: all 0.3s;
}

.stat-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0,0,0,0.1);
}

.stat-icon {
    width: 55px;
    height: 55px;
    border-radius: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    color: white;
}

.stat-icon.total-users {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.stat-icon.total-balance {
    background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
}

.stat-icon.total-credited {
    background: linear-gradient(135deg, #f6ad55 0%, #ed8936 100%);
}

.stat-icon.total-debited {
    background: linear-gradient(135deg, #9f7aea 0%, #805ad5 100%);
}

.stat-info h3 {
    font-size: 0.85rem;
    font-weight: 500;
    color: #64748b;
    margin-bottom: 0.25rem;
}

.stat-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
}

/* Tabs */
.wallet-tabs {
    display: flex;
    gap: 1rem;
    margin-bottom: 1.5rem;
    background: white;
    padding: 0.5rem;
    border-radius: 60px;
    width: fit-content;
}

.tab-btn {
    padding: 0.6rem 1.5rem;
    background: transparent;
    border: none;
    border-radius: 50px;
    font-size: 0.9rem;
    font-weight: 500;
    color: #64748b;
    cursor: pointer;
    transition: all 0.3s;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.tab-btn.active {
    background: linear-gradient(135deg, #ec3134, #c2410c);
    color: white;
}

/* Table Header */
.table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.search-box {
    display: flex;
    align-items: center;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 40px;
    padding: 0.5rem 1rem;
    width: 300px;
}

.search-box i {
    color: #94a3b8;
    margin-right: 0.5rem;
}

.search-box input {
    border: none;
    width: 100%;
    outline: none;
}

/* Tables */
.table-container {
    background: white;
    border-radius: 20px;
    border: 1px solid #e2e8f0;
    overflow-x: auto;
}

.wallet-table, .transactions-table {
    width: 100%;
    border-collapse: collapse;
    min-width: 800px;
}

.wallet-table th, .transactions-table th {
    text-align: left;
    padding: 1rem 1.25rem;
    background: #f8fafc;
    font-size: 0.75rem;
    font-weight: 600;
    color: #475569;
    text-transform: uppercase;
    border-bottom: 1px solid #e2e8f0;
}

.wallet-table td, .transactions-table td {
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
}

/* User Cell */
.user-cell {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.user-avatar {
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, #ec3134, #c2410c);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 1rem;
}

.user-avatar.small {
    width: 35px;
    height: 35px;
    font-size: 0.9rem;
}

.user-avatar.large {
    width: 60px;
    height: 60px;
    font-size: 1.5rem;
}

.user-details {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
}

.user-details strong {
    font-size: 0.9rem;
    color: #1e293b;
}

.user-details span {
    font-size: 0.7rem;
    color: #64748b;
}

/* Balance Cells */
.balance-amount {
    font-weight: 700;
    color: #2e7d32;
    font-size: 1rem;
}

.credited-amount {
    color: #166534;
    font-weight: 500;
}

.debited-amount {
    color: #991b1b;
    font-weight: 500;
}

/* Amount Cell */
.amount-cell.credit {
    color: #166534;
    font-weight: 600;
}

.amount-cell.debit {
    color: #991b1b;
    font-weight: 600;
}

/* Transaction Type */
.tx-type {
    display: inline-block;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.7rem;
    font-weight: 500;
    text-transform: capitalize;
}

.tx-type.bonus, .tx-type.reward {
    background: #dcfce7;
    color: #166534;
}

.tx-type.refund {
    background: #dbeafe;
    color: #1e40af;
}

.tx-type.payment {
    background: #fee2e2;
    color: #991b1b;
}

.tx-id-value {
    font-family: monospace;
    font-size: 0.7rem;
    color: #64748b;
}

/* Actions */
.actions-cell {
    white-space: nowrap;
}

.action-btn {
    padding: 0.5rem 1rem;
    background: #f1f5f9;
    border: none;
    border-radius: 8px;
    color: #64748b;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8rem;
    transition: all 0.2s;
}

.action-btn.add-bonus:hover {
    background: #e8f5e9;
    color: #2e7d32;
}

/* Modal */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.5);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.modal-container {
    background: white;
    border-radius: 24px;
    width: 90%;
    max-width: 500px;
    animation: slideUp 0.3s ease;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid #e2e8f0;
}

.modal-header h2 {
    font-size: 1.2rem;
    color: #1e293b;
    margin: 0;
}

.modal-body {
    padding: 1.5rem;
}

.user-info {
    display: flex;
    gap: 1rem;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #e2e8f0;
}

.user-info-details h3 {
    font-size: 1rem;
    margin-bottom: 0.25rem;
}

.user-info-details p {
    font-size: 0.8rem;
    color: #64748b;
    margin: 0.2rem 0;
}

.current-balance {
    font-weight: 600;
    color: #2e7d32;
    margin-top: 0.5rem !important;
}

.form-group {
    margin-bottom: 1rem;
}

.form-group label {
    display: block;
    font-size: 0.85rem;
    font-weight: 500;
    color: #475569;
    margin-bottom: 0.5rem;
}

.form-group input, .form-group textarea {
    width: 100%;
    padding: 0.6rem 0.8rem;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    font-size: 0.85rem;
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 1rem;
}

.btn-cancel, .btn-submit {
    padding: 0.6rem 1.2rem;
    border-radius: 8px;
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    border: none;
}

.btn-cancel {
    background: #f1f5f9;
    color: #475569;
}

.btn-submit {
    background: #2e7d32;
    color: white;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
}

.btn-submit:hover {
    background: #1b5e20;
}

/* Loading State */
.loading-state {
    text-align: center;
    padding: 3rem;
}

.spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e2e8f0;
    border-top-color: #ec3134;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 1rem;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

/* No Data */
.no-data {
    text-align: center;
    padding: 3rem !important;
    color: #94a3b8;
}

.no-data i {
    font-size: 3rem;
    margin-bottom: 1rem;
}

/* Responsive */
@media (max-width: 1024px) {
    .admin-main {
        margin-left: 0;
        padding: 1rem;
    }
    
    .wallet-stats {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 768px) {
    .wallet-stats {
        grid-template-columns: 1fr;
    }
    
    .wallet-tabs {
        width: 100%;
    }
    
    .tab-btn {
        flex: 1;
        justify-content: center;
    }
    
    .search-box {
        width: 100%;
    }
}
            `}</style>
        </div>

    );
};

export default AdminWallet;
